/**
 * Safe markdown subset → plain-object AST (frontend plan §7). Framework-free
 * (copied to next-mapit); `components/MarkdownMessage.js` maps the AST to MUI
 * elements.
 *
 * Supported: paragraphs (blank line), line breaks (single newline),
 * `**strong**`, `*em*` / `_em_`, `` `code` ``, `-` / `*` bullets, `1.`
 * numbered lists, `#`…`######` headings, `[text](url)`, `<url>` and bare URLs.
 * A link survives only when `linkPolicy` allows it; otherwise its text stays
 * as plain text. There is no HTML: `<b>` is just text.
 *
 * It re-parses the whole answer on every animation frame while it streams, so:
 * - it is linear in the input (no backtracking regexes, cached look-aheads);
 * - an unclosed marker (`**bold`, `` `code ``, `[text](https://ma`) stays
 *   literal until its closer arrives;
 * - it never throws, whatever the prefix.
 */
import { getAllowedHref } from "./linkPolicy";

/** @typedef {{ type: "text", value: string }} TextNode */
/** @typedef {{ type: "code", value: string }} CodeNode */
/** @typedef {{ type: "br" }} BreakNode */
/** @typedef {{ type: "strong" | "em", children: InlineNode[] }} EmphasisNode */
/**
 * @typedef {{ type: "link", href: string, children: InlineNode[] }} LinkNode
 * `href` is the normalized URL that passed `getAllowedHref`.
 */
/** @typedef {TextNode | CodeNode | BreakNode | EmphasisNode | LinkNode} InlineNode */
/**
 * @typedef {{ type: "listItem", children: Array<InlineNode | ListNode> }} ListItemNode
 * Inline content, `br` for continuation lines, and nested lists.
 */
/**
 * @typedef {{ type: "list", ordered: boolean, start: number | null, items: ListItemNode[] }} ListNode
 * `start` is the first item's number for an ordered list, null for bullets.
 */
/** @typedef {{ type: "paragraph", children: InlineNode[] }} ParagraphNode */
/**
 * @typedef {{ type: "heading", depth: number, children: InlineNode[] }} HeadingNode
 * Rendered as a bold paragraph (no real headings inside a bubble).
 */
/** @typedef {ParagraphNode | HeadingNode | ListNode} BlockNode */
/** @typedef {{ type: "root", children: BlockNode[] }} RootNode */

const ASCII_PUNCTUATION = "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~";
// Non-ASCII punctuation likely to touch an emphasis marker in Arabic/English
// answers: Arabic comma/semicolon/question mark/full stop, guillemets, dashes,
// curly quotes, bullets, ellipsis.
const UNICODE_PUNCTUATION = /[¡§«¶·»¿،؛؟٪-٭۔‐-‧‰-⁞、-〃]/;
const WHITESPACE = /\s/;
const ASCII_WORD = /[A-Za-z0-9]/;

const BULLET_MARKER = /^[-*+•][ \t]+/;
const ORDERED_MARKER = /^(\d{1,9})[.)][ \t]+/;

/** Extra indent (columns) that nests a list item under the previous one. */
const NESTED_LIST_INDENT = 2;
/** Deeper items stay at this depth; the panel is only 420 px wide. */
const MAX_LIST_DEPTH = 4;

/** Dropped from the end of a bare URL ("see https://mapit.sa."). */
const TRAILING_URL_PUNCTUATION = ".,:;!?*_~'\"،؛؟";

// The line edges count as whitespace when deciding if a `*`/`_` run flanks a word.
const isWhitespace = (ch) => ch === "" || WHITESPACE.test(ch);
const isPunctuation = (ch) =>
  ch !== "" && (ASCII_PUNCTUATION.includes(ch) || UNICODE_PUNCTUATION.test(ch));

const textNode = (value) => ({ type: "text", value });

/** Appends, merging adjacent text nodes and dropping empty ones. */
function appendInline(children, node) {
  if (node.type !== "text") {
    children.push(node);
    return;
  }
  if (!node.value) return;
  const last = children[children.length - 1];
  if (last && last.type === "text") {
    children[children.length - 1] = textNode(last.value + node.value);
  } else {
    children.push(node);
  }
}

// A loop, not push(...nodes): spreading a huge array overflows the stack.
function appendAll(children, nodes) {
  for (let k = 0; k < nodes.length; k++) appendInline(children, nodes[k]);
}

// ---------------------------------------------------------------------------
// Inline level
// ---------------------------------------------------------------------------

function runEnd(src, start, ch) {
  let end = start;
  while (end < src.length && src[end] === ch) end++;
  return end;
}

/**
 * Finds the start of the next backtick run of exactly `length` at or after
 * `from` (-1 if none). `from` must never decrease between calls, which lets
 * each per-length cursor only move forward: O(n) over the whole line.
 */
function createCodeCloserIndex(src) {
  const runsByLength = new Map();
  for (let i = 0; i < src.length;) {
    if (src[i] !== "`") {
      i++;
      continue;
    }
    const end = runEnd(src, i, "`");
    const length = end - i;
    if (!runsByLength.has(length)) {
      runsByLength.set(length, { starts: [], cursor: 0 });
    }
    runsByLength.get(length).starts.push(i);
    i = end;
  }

  return (length, from) => {
    const runs = runsByLength.get(length);
    if (!runs) return -1;
    while (
      runs.cursor < runs.starts.length &&
      runs.starts[runs.cursor] < from
    ) {
      runs.cursor++;
    }
    return runs.cursor < runs.starts.length ? runs.starts[runs.cursor] : -1;
  };
}

// CommonMark: strip one space on each side when both exist, unless it's all spaces.
function codeSpanValue(raw) {
  if (
    raw.length >= 2 &&
    raw[0] === " " &&
    raw[raw.length - 1] === " " &&
    /[^ ]/.test(raw)
  ) {
    return raw.slice(1, -1);
  }
  return raw;
}

/**
 * @typedef {Object} Delimiter A run of `*` or `_` that may open/close emphasis.
 * @property {"delimiter"} type
 * @property {"*" | "_"} char
 * @property {number} length Original run length (for the rule of 3).
 * @property {number} count Characters not yet used by a match.
 * @property {boolean} canOpen
 * @property {boolean} canClose
 * @property {Array<"strong" | "em">} opens Left to right, outermost first.
 * @property {Array<"strong" | "em">} closes Left to right, innermost first.
 * @property {Delimiter | null} prev
 * @property {Delimiter | null} next
 * @property {number} position Index in the delimiter list.
 */

/** CommonMark flanking rules; `_` can't open/close inside a word (snake_case). */
function createDelimiter(src, start, end) {
  const char = src[start];
  const before = start > 0 ? src[start - 1] : "";
  const after = end < src.length ? src[end] : "";

  const leftFlanking =
    !isWhitespace(after) &&
    (!isPunctuation(after) || isWhitespace(before) || isPunctuation(before));
  const rightFlanking =
    !isWhitespace(before) &&
    (!isPunctuation(before) || isWhitespace(after) || isPunctuation(after));

  return {
    type: "delimiter",
    char,
    length: end - start,
    count: end - start,
    canOpen:
      char === "*"
        ? leftFlanking
        : leftFlanking && (!rightFlanking || isPunctuation(before)),
    canClose:
      char === "*"
        ? rightFlanking
        : rightFlanking && (!leftFlanking || isPunctuation(after)),
    opens: [],
    closes: [],
    prev: null,
    next: null,
    position: 0,
  };
}

// CommonMark "rule of 3": `*foo**bar*` must not pair the `*` with the `**`.
const breaksRuleOfThree = (opener, closer) =>
  (opener.canClose || closer.canOpen) &&
  (opener.length + closer.length) % 3 === 0 &&
  !(opener.length % 3 === 0 && closer.length % 3 === 0);

function unlink(delimiter) {
  if (delimiter.prev) delimiter.prev.next = delimiter.next;
  if (delimiter.next) delimiter.next.prev = delimiter.prev;
}

/**
 * CommonMark's "process emphasis" over a linked list of delimiters. Records
 * each match on the delimiters (`opens` / `closes`) instead of rebuilding the
 * tree; matches are always properly nested, so `buildInlineTree` can replay
 * them with a stack. `openersBottom` keeps it linear.
 */
function processEmphasis(delimiters) {
  delimiters.forEach((delimiter, index) => {
    delimiter.position = index;
    delimiter.prev = delimiters[index - 1] || null;
    delimiter.next = delimiters[index + 1] || null;
  });

  const openersBottom = new Map();
  let closer = delimiters[0] || null;

  while (closer) {
    if (!closer.canClose) {
      closer = closer.next;
      continue;
    }

    const key = `${closer.char}${closer.canOpen ? 1 : 0}${closer.length % 3}`;
    const bottom = openersBottom.has(key) ? openersBottom.get(key) : -1;
    let opener = closer.prev;
    while (
      opener &&
      opener.position > bottom &&
      !(
        opener.char === closer.char &&
        opener.canOpen &&
        !breaksRuleOfThree(opener, closer)
      )
    ) {
      opener = opener.prev;
    }

    if (opener && opener.position > bottom) {
      const used = opener.count >= 2 && closer.count >= 2 ? 2 : 1;
      const kind = used === 2 ? "strong" : "em";
      opener.count -= used;
      closer.count -= used;
      opener.opens.unshift(kind);
      closer.closes.push(kind);
      // Delimiters between the pair can no longer match: they stay literal.
      opener.next = closer;
      closer.prev = opener;
      if (opener.count === 0) unlink(opener);
      if (closer.count === 0) {
        const next = closer.next;
        unlink(closer);
        closer = next;
      }
    } else {
      // Nothing at or below here can ever match a closer with this key.
      openersBottom.set(key, closer.prev ? closer.prev.position : -1);
      const next = closer.next;
      if (!closer.canOpen) unlink(closer);
      closer = next;
    }
  }
}

/** Replays the matches recorded by `processEmphasis` into nested nodes. */
function buildInlineTree(items) {
  /** @type {InlineNode[]} */
  const root = [];
  const stack = [root];

  items.forEach((item) => {
    if (item.type !== "delimiter") {
      appendInline(stack[stack.length - 1], item);
      return;
    }
    // A closer uses the run's leftmost characters, an opener its rightmost;
    // whatever is left over in between is literal.
    item.closes.forEach(() => {
      if (stack.length > 1) stack.pop();
    });
    appendInline(
      stack[stack.length - 1],
      textNode(item.char.repeat(item.count)),
    );
    item.opens.forEach((kind) => {
      const node = { type: kind, children: [] };
      stack[stack.length - 1].push(node);
      stack.push(node.children);
    });
  });

  return root;
}

/** Length of an `http(s)://` scheme at `index` (case-insensitive), else 0. */
function urlSchemeLength(src, index) {
  const head = src.slice(index, index + 8).toLowerCase();
  if (head === "https://") return 8;
  if (head.startsWith("http://")) return 7;
  return 0;
}

/**
 * GFM-style: drop trailing punctuation (Arabic too), and a `)` / `]` only when
 * unbalanced, so `(https://mapit.sa/a)` and `…/wiki/Foo_(bar)` both work.
 */
function trimUrlEnd(src, start, end) {
  const counts = { "(": 0, ")": 0, "[": 0, "]": 0 };
  for (let k = start; k < end; k++) {
    if (src[k] in counts) counts[src[k]]++;
  }

  let trimmed = end;
  while (trimmed > start) {
    const ch = src[trimmed - 1];
    if (TRAILING_URL_PUNCTUATION.includes(ch)) {
      trimmed--;
    } else if (ch === ")" && counts[")"] > counts["("]) {
      counts[")"]--;
      trimmed--;
    } else if (ch === "]" && counts["]"] > counts["["]) {
      counts["]"]--;
      trimmed--;
    } else {
      break;
    }
  }
  return trimmed;
}

/** An allowed URL becomes a link showing the URL itself; any other stays text. */
function urlNodes(url) {
  const href = getAllowedHref(url);
  return href
    ? [{ type: "link", href, children: [textNode(url)] }]
    : [textNode(url)];
}

/** A disallowed `[text](url)` keeps its text and drops the URL. */
function linkNodes(url, children) {
  const href = getAllowedHref(url);
  if (!href) return children;
  return [
    {
      type: "link",
      href,
      children: children.length ? children : [textNode(url)],
    },
  ];
}

/**
 * Matches `[text](url)`, `<url>` and bare URLs. The look-aheads are cached so
 * repeated failed attempts (`[[[[…`, `[a](b[a](b…`) stay linear; callers must
 * ask for increasing positions.
 */
function createLinkMatcher(src) {
  let bracketSearch = null;
  let destinationScan = null;

  const findCloseBracket = (from) => {
    if (
      !bracketSearch ||
      (bracketSearch.at !== -1 && bracketSearch.at < from)
    ) {
      bracketSearch = { at: src.indexOf("]", from) };
    }
    return bracketSearch.at;
  };

  // `(` already consumed. Balanced parentheses are part of the URL; spaces
  // around it are allowed; titles (`"…"`) are not supported.
  const scanDestination = (start) => {
    if (destinationScan && destinationScan.start === start) {
      return destinationScan.result;
    }
    let result = null;
    let k = start;
    while (k < src.length && (src[k] === " " || src[k] === "\t")) k++;
    const urlStart = k;
    let depth = 0;
    for (; k < src.length; k++) {
      const ch = src[k];
      if (ch === "(") depth++;
      else if (ch === ")") {
        if (depth === 0) break;
        depth--;
      } else if (
        ch === "<" ||
        ch === ">" ||
        ch === "[" ||
        ch === "]" ||
        isWhitespace(ch)
      ) {
        break;
      }
    }
    const urlEnd = k;
    while (k < src.length && (src[k] === " " || src[k] === "\t")) k++;
    if (src[k] === ")") {
      result = { url: src.slice(urlStart, urlEnd), end: k + 1 };
    }
    destinationScan = { start, result };
    return result;
  };

  const matchLink = (index) => {
    const close = findCloseBracket(index + 1);
    if (close === -1 || src[close + 1] !== "(") return null;
    const destination = scanDestination(close + 2);
    if (!destination) return null;
    // Link text can't contain links, so this recursion is one level deep.
    const children = parseInline(src.slice(index + 1, close), true);
    return {
      nodes: linkNodes(destination.url, children),
      end: destination.end,
    };
  };

  const matchAngleUrl = (index) => {
    const schemeLength = urlSchemeLength(src, index + 1);
    if (!schemeLength) return null;
    let end = index + 1 + schemeLength;
    while (
      end < src.length &&
      src[end] !== ">" &&
      src[end] !== "<" &&
      !isWhitespace(src[end])
    ) {
      end++;
    }
    if (src[end] !== ">" || end === index + 1 + schemeLength) return null;
    return { nodes: urlNodes(src.slice(index + 1, end)), end: end + 1 };
  };

  const matchBareUrl = (index) => {
    if (index > 0 && ASCII_WORD.test(src[index - 1])) return null;
    const schemeLength = urlSchemeLength(src, index);
    if (!schemeLength) return null;
    let end = index + schemeLength;
    while (
      end < src.length &&
      src[end] !== "<" &&
      src[end] !== ">" &&
      !isWhitespace(src[end])
    ) {
      end++;
    }
    end = trimUrlEnd(src, index, end);
    // Just the scheme so far (still streaming): leave it as text.
    if (end === index + schemeLength) return null;
    return { nodes: urlNodes(src.slice(index, end)), end };
  };

  return (index) => {
    switch (src[index]) {
      case "[":
        return matchLink(index);
      case "<":
        return matchAngleUrl(index);
      case "h":
      case "H":
        return matchBareUrl(index);
      default:
        return null;
    }
  };
}

/**
 * Parses one line of inline content. Code spans bind tightest, then links and
 * URLs (atomic: their `*`/`_` are never emphasis), then emphasis via the
 * CommonMark delimiter algorithm. Backslash escapes ASCII punctuation.
 *
 * @param {string} src
 * @param {boolean} [inLink] Inside link text: no nested links or URLs.
 * @returns {InlineNode[]}
 */
function parseInline(src, inLink = false) {
  const items = [];
  const delimiters = [];
  const findCodeCloser = src.includes("`") ? createCodeCloserIndex(src) : null;
  const matchLink = inLink ? null : createLinkMatcher(src);
  let text = "";
  let i = 0;

  const pushItem = (item) => {
    if (text) {
      items.push(textNode(text));
      text = "";
    }
    items.push(item);
  };

  while (i < src.length) {
    const ch = src[i];

    if (
      ch === "\\" &&
      i + 1 < src.length &&
      ASCII_PUNCTUATION.includes(src[i + 1])
    ) {
      text += src[i + 1];
      i += 2;
      continue;
    }

    if (ch === "`") {
      const end = runEnd(src, i, "`");
      const closer = findCodeCloser(end - i, end);
      if (closer === -1) {
        text += src.slice(i, end);
        i = end;
      } else {
        pushItem({
          type: "code",
          value: codeSpanValue(src.slice(end, closer)),
        });
        i = closer + (end - i);
      }
      continue;
    }

    if (ch === "*" || ch === "_") {
      const end = runEnd(src, i, ch);
      const delimiter = createDelimiter(src, i, end);
      if (delimiter.canOpen || delimiter.canClose) {
        pushItem(delimiter);
        delimiters.push(delimiter);
      } else {
        text += src.slice(i, end);
      }
      i = end;
      continue;
    }

    const link = matchLink && matchLink(i);
    if (link) {
      link.nodes.forEach(pushItem);
      i = link.end;
      continue;
    }

    text += ch;
    i++;
  }
  if (text) items.push(textNode(text));

  processEmphasis(delimiters);
  return buildInlineTree(items);
}

// ---------------------------------------------------------------------------
// Block level
// ---------------------------------------------------------------------------

/** Leading whitespace width (tabs to the next multiple of 4) and the rest. */
function splitIndent(line) {
  let indent = 0;
  let i = 0;
  for (; i < line.length; i++) {
    if (line[i] === " ") indent++;
    else if (line[i] === "\t") indent += 4 - (indent % 4);
    else break;
  }
  return { indent, rest: line.slice(i) };
}

// `---`, `* * *`, `___`: not in the subset, but models emit them. They only
// end the current block (no visual separator inside a bubble).
function isThematicBreak(rest) {
  const marker = rest[0];
  if (marker !== "-" && marker !== "*" && marker !== "_") return false;
  let count = 0;
  for (let i = 0; i < rest.length; i++) {
    if (rest[i] === marker) count++;
    else if (rest[i] !== " " && rest[i] !== "\t") return false;
  }
  return count >= 3;
}

/** `#`…`######` + space; a bare `##` (mid-stream) is an empty heading. */
function matchHeading(rest) {
  let depth = 0;
  while (depth < rest.length && rest[depth] === "#") depth++;
  if (depth === 0 || depth > 6) return null;
  if (depth < rest.length && rest[depth] !== " " && rest[depth] !== "\t") {
    return null;
  }

  let content = rest.slice(depth).trim();
  // Optional closing sequence: "## Title ##".
  let end = content.length;
  while (end > 0 && content[end - 1] === "#") end--;
  if (end === 0) content = "";
  else if (end < content.length && /[ \t]/.test(content[end - 1])) {
    content = content.slice(0, end).trim();
  }
  return { depth, content };
}

// The marker must be followed by a space, so "*italic*" / "**bold**" / "1.5"
// at the start of a line never flash as list items mid-stream.
function matchListItem(rest) {
  const bullet = BULLET_MARKER.exec(rest);
  if (bullet) {
    return {
      ordered: false,
      start: null,
      content: rest.slice(bullet[0].length).trim(),
    };
  }
  const ordered = ORDERED_MARKER.exec(rest);
  if (ordered) {
    return {
      ordered: true,
      start: Number(ordered[1]),
      content: rest.slice(ordered[0].length).trim(),
    };
  }
  return null;
}

const lastItem = (list) => list.items[list.items.length - 1];

const createList = (ordered, start, item) => ({
  type: "list",
  ordered,
  start: ordered ? start : null,
  items: [item],
});

/**
 * `state.lists` is the stack of open lists, outermost first, each with the
 * indent of its marker. A marker at least `NESTED_LIST_INDENT` deeper than
 * the innermost list nests under its last item; a shallower one closes the
 * deeper lists; a different kind at the same level starts a sibling list.
 */
function addListItem(state, indent, { ordered, start, content }) {
  const item = { type: "listItem", children: parseInline(content) };
  const { lists } = state;
  while (lists.length > 1 && lists[lists.length - 1].indent > indent) {
    lists.pop();
  }
  const top = lists[lists.length - 1];

  if (
    top &&
    indent >= top.indent + NESTED_LIST_INDENT &&
    lists.length < MAX_LIST_DEPTH
  ) {
    const list = createList(ordered, start, item);
    lastItem(top.node).children.push(list);
    lists.push({ node: list, indent });
    return;
  }

  if (top && top.node.ordered === ordered) {
    top.node.items.push(item);
    return;
  }

  const list = createList(ordered, start, item);
  if (lists.length > 1) {
    lists.pop();
    lastItem(lists[lists.length - 1].node).children.push(list);
    lists.push({ node: list, indent });
    return;
  }
  state.root.children.push(list);
  state.lists = [{ node: list, indent }];
}

/** An indented non-marker line continues the item it is indented under. */
function continueListItem(state, indent, content) {
  const { lists } = state;
  while (lists.length > 1 && lists[lists.length - 1].indent >= indent) {
    lists.pop();
  }
  const item = lastItem(lists[lists.length - 1].node);
  const last = item.children[item.children.length - 1];
  if (last && last.type !== "list") item.children.push({ type: "br" });
  appendAll(item.children, parseInline(content));
}

function parseLine(state, line) {
  const { indent, rest } = splitIndent(line);

  if (!rest.trim()) {
    // Ends a paragraph; a list survives it ("loose" lists keep one numbering).
    state.paragraph = null;
    return;
  }

  if (isThematicBreak(rest)) {
    state.paragraph = null;
    state.lists = [];
    return;
  }

  const heading = matchHeading(rest);
  if (heading) {
    state.paragraph = null;
    state.lists = [];
    if (heading.content) {
      state.root.children.push({
        type: "heading",
        depth: heading.depth,
        children: parseInline(heading.content),
      });
    }
    return;
  }

  const item = matchListItem(rest);
  if (item) {
    state.paragraph = null;
    addListItem(state, indent, item);
    return;
  }

  const content = rest.trim();
  if (state.lists.length && indent > 0) {
    continueListItem(state, indent, content);
    return;
  }

  // An unindented line after a list is a new paragraph, not a lazy
  // continuation: models don't hard-wrap items, they write a closing sentence.
  state.lists = [];
  if (state.paragraph) {
    state.paragraph.children.push({ type: "br" });
    appendAll(state.paragraph.children, parseInline(content));
    return;
  }
  state.paragraph = { type: "paragraph", children: parseInline(content) };
  state.root.children.push(state.paragraph);
}

/**
 * Parses an assistant answer (complete or still streaming) into an AST.
 * Inline markers never span lines.
 *
 * @param {unknown} text
 * @returns {RootNode} Never throws; non-strings give an empty root.
 */
export function parseMarkdown(text) {
  /** @type {RootNode} */
  const root = { type: "root", children: [] };
  if (typeof text !== "string" || !text) return root;

  const state = { root, paragraph: null, lists: [] };
  text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .forEach((line) => parseLine(state, line));
  return root;
}
